import { Component, inject, OnInit, signal, viewChild } from "@angular/core";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { ToastrService } from "ngx-toastr";

import { ConfirmationModalComponent, DataTableComponent } from "../../../shared/components";
import { genericFloatButtons } from "../../../shared/components/general/float-action";
import { FaqsCategoryEditorComponent } from "../../../shared/components/modals/faqs-category-editor/faqs-category-editor";
import { Constants } from "../../../shared/constants/constants";
import { Language, PageMode } from "../../../shared/constants/enums";
import { TableButtonClicked } from "../../../shared/models";
import { FaqCategory } from "../../../shared/models/online-help-model";
import { AppService } from "../../../shared/services";
import { OnlineHelpService } from "../../../shared/services/online-help-service";

@Component({
  selector: "adaa-faqs-category",
  imports: [DataTableComponent, TranslatePipe, FaqsCategoryEditorComponent],
  templateUrl: "./faqs-category.html",
  styleUrl: "./faqs-category.scss",
})
export class FaqsCategoryComponent implements OnInit {
  private _modalService = inject(NgbModal);
  private _appService = inject(AppService);
  private _onlineHelpService = inject(OnlineHelpService);
  private _toastrService = inject(ToastrService);
  private _translateService = inject(TranslateService);

  Constants = Constants;
  pageMode = signal<string>("");
  adaaDataTable = viewChild.required<DataTableComponent>("adaaDataTable");
  faqsCategoryEditor = viewChild.required<FaqsCategoryEditorComponent>("category");
  currentIndex: number | undefined;
  private readonly _genericFloatButtons = genericFloatButtons();

  public ngOnInit(): void {
    this._floatActions();
  }

  public addNew() {
    this.pageMode.set(PageMode.create);
    this.faqsCategoryEditor().open();
  }

  public getAction(value: TableButtonClicked) {
    switch (value.event) {
      case "edit":
        this.pageMode.set(PageMode.edit);
        this.faqsCategoryEditor().faqsCategoryForm.patchValue(value.data);
        this.faqsCategoryEditor().open();
        this.currentIndex = value.index;
        break;
      case "delete":
        this._deleteCategory(value.data.id);
        break;

      default:
        break;
    }
  }

  private _deleteCategory(id: number) {
    const modal = this._modalService.open(ConfirmationModalComponent, {
      centered: true,
      size: "md",
      modalDialogClass: this._appService.language() === Language.Arabic ? "modal-rtl" : "modal-ltr",
    });

    modal.componentInstance.header = "help.faqs-category.delete_yes_no_title";
    modal.componentInstance.title = "help.faqs-category.delete_yes_no_information";

    modal.result.then((e) => {
      if (e) {
        this._onlineHelpService.deleteFAQCategory(id).subscribe({
          next: (response) => {
            if (response.inError) return;
            this._toastrService.success(this._translateService.instant("notification.success.remove"));
            this.adaaDataTable().loadTableData();
          },
        });
      }
    });
  }

  public submit(value: FaqCategory) {
    const result = {
      ...(this.currentIndex ? this.adaaDataTable().tableData()[this.currentIndex] : {}),
      ...value,
      icon: value.iconAttachment?.id,
    };

    delete result.planId;
    delete result.updateUser;

    const url =
      this.pageMode() === PageMode.create
        ? this._onlineHelpService.createFAQCategory(result)
        : this._onlineHelpService.updateFAQCategory(result);

    url.subscribe({
      next: (response) => {
        if (response.inError) return;
        this._toastrService.success(this._translateService.instant("notification.success.save"));
        this.adaaDataTable().loadTableData();
      },
    });
  }

  private _floatActions(): void {
    this._genericFloatButtons([
      {
        key: "helpdesk",
        data: {
          url: "/helpdesk/create",
        },
      },
      {
        key: "print",
        data: undefined,
      },
    ]);
  }
}
